import React, { useCallback, useContext, useState } from "react";
import { AssetRecordType, TLShapeId, useEditor } from "@tldraw/tldraw";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useParams } from "react-router-dom";
import { useStore } from "store";
import { useBrandKitStore } from "store/brandKit";
import { uploadBrandKitAsset } from "pages/flyer/builder/services/brandKitUploadService";
import { FORMATS } from "pages/flyer/builder/constants";
import { PossibleFlyerType } from "pages/flyer/builder/drawer";
import BrandKitDropZone from "./BrandKitDropZone";
import { useImageUpload } from "pages/flyer/builder/drawer/contexts/ImageUploadContext";
import { SnackBarContext } from "components/snack-bar";
import { getSignedS3Url } from "pages/flyer/builder/utils/getSignedS3Url";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";
import "pages/flyer/builder/drawer/components/styles.css";

type AssetType = "image" | "logo" | "icon";

const STORE_KEYS: Record<AssetType, "images" | "logos" | "icons"> = {
  image: "images",
  logo: "logos",
  icon: "icons",
};

const ACCEPT: Record<AssetType, string> = {
  image: "image/*,.pdf",
  logo: "image/*,.svg,.pdf",
  icon: ".svg,image/*,.pdf",
};

const BrandKitAssetsPanel: React.FC = () => {
  const editor = useEditor();
  const isEmptyState = useIsBuilderEmptyState();
  const { brandKitAssetsPanel } = useStore();
  const { format } = useParams() as { format?: string };
  const currentFormatConfig = FORMATS[format as PossibleFlyerType];
  const brandKit = useBrandKitStore();
  const [uploading, setUploading] = useState(false);
  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = useContext(SnackBarContext);

  const assetType = brandKitAssetsPanel as AssetType;
  const storeKey = STORE_KEYS[assetType];
  const items = brandKit[storeKey]?.items ?? [];

  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      startUpload(`Saving ${assetType} to Brand Kit...`);
      try {
        const result = await uploadBrandKitAsset(file, assetType);
        brandKit.addAsset(assetType, {
          id: `temp-${Date.now()}`,
          name: result.name,
          s3Url: result.s3Url,
          s3Key: result.s3Key,
          mimeType: result.mimeType,
          fileSize: result.fileSize,
        });
        await brandKit.loadBrandKit();
      } catch (err) {
        console.error(`Brand ${assetType} upload failed:`, err);
        runSnackBar({
          msg: `Failed to save ${assetType}. Please try again.`,
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        setUploading(false);
        endUpload();
      }
    },
    [assetType, brandKit, startUpload, endUpload]
  );

  const handleItemClick = useCallback(
    async (item: any, imgEl?: HTMLImageElement) => {
      if (!editor || isEmptyState) return;
      startUpload(`Adding ${assetType} to canvas...`);
      try {
        // Icons: SVGs get added as customSvg shape
        if (assetType === "icon") {
          const isSvg = item.mimeType === "image/svg+xml" || item.s3Url?.endsWith(".svg");
          if (isSvg) {
            const signedSvgUrl = await getSignedS3Url(item.s3Url);
            const res = await fetch(signedSvgUrl);
            const svgContent = await res.text();
            const { createShapeId } = await import("@tldraw/editor");
            const shapeId = createShapeId();
            const { width, height } = editor.getViewportScreenBounds();
            const center = editor.screenToPage({ x: width / 2, y: height / 2 });

            editor.createShape({
              id: shapeId,
              type: "customSvg",
              x: center.x - 125 / 2,
              y: center.y - 125 / 2,
              props: {
                w: 125,
                h: 125,
                svgPath: svgContent,
                fill: "#000000",
                colorMix: { "0": { color: "#000000", isHidden: false } },
              },
            });
            editor.setCurrentTool("select");
            editor.select(shapeId);
            return;
          }
        }

        // Always use a signed URL to avoid CORS / 403 errors on direct S3 access
        const signedSrc = await getSignedS3Url(item.s3Url);

        // Load the image with the signed URL to get real pixel dimensions
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = signedSrc;

          img.onload = () => {
            const imgW = img.naturalWidth;
            const imgH = img.naturalHeight;
            const MAX_DISPLAY = 1000;
            const scaleFactor = Math.max(imgW, imgH) > MAX_DISPLAY ? MAX_DISPLAY / Math.max(imgW, imgH) : 1;
            const displayW = Math.round(imgW * scaleFactor);
            const displayH = Math.round(imgH * scaleFactor);

            const assetId = AssetRecordType.createId();
            const { width, height } = editor.getViewportScreenBounds();
            const center = editor.screenToPage({ x: width / 2, y: height / 2 });

            editor.createAssets([
              {
                id: assetId,
                type: "image",
                typeName: "asset",
                props: {
                  name: item.name,
                  src: item.s3Url,
                  w: imgW,
                  h: imgH,
                  mimeType: item.mimeType || "image/png",
                  isAnimated: false,
                },
                meta: { name: item.name },
              },
            ]);

            const shapeId = `shape:bk-${assetType}-${item.id}-${Date.now()}` as TLShapeId;
            editor.createShapes([
              {
                id: shapeId,
                type: "customImage",
                props: { assetId, w: displayW, h: displayH },
                x: center.x - displayW / 2,
                y: center.y - displayH / 2,
              },
            ]);
            editor.setCurrentTool("select");
            editor.select(shapeId);
            resolve();
          };

          img.onerror = () => {
            console.error("Failed to load brand kit image:", signedSrc);
            reject(new Error("Failed to load brand kit image"));
          };
        });
      } catch (err) {
        console.error("Error adding asset to canvas:", err);
        runSnackBar({
          msg: `Failed to add ${assetType} to canvas. Please try again.`,
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [editor, isEmptyState, assetType, startUpload, endUpload]
  );

  const handleRemoveItem = useCallback(
    async (id: string) => {
      await brandKit.removeAsset(assetType, id);
    },
    [assetType, brandKit]
  );

  if (!assetType) return null;

  return (
    <div className="flex flex-col h-full bg-white w-[240px] pl-4 pr-4">
      {/* Drop zone */}
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <BrandKitDropZone type={assetType} accept={ACCEPT[assetType]} onFile={handleUploadFile} uploading={uploading} />
      </div>

      {/* Asset grid - 2 cols, 94x94 */}
      <ul className="grid grid-cols-2 gap-3 pt-1 overflow-y-auto" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((item: any) => (
          <li
            key={item.id}
            className="rounded-[8px] bg-slate-100 overflow-hidden w-[94px] h-[94px] hover:shadow-[0px_2px_10px_2px_rgba(0,0,0,_0.1)] transition-all duration-300 brand-kit-gallery-item"
            style={{ position: "relative" }}
          >
            <img
              className="object-cover cursor-pointer transition-all w-full h-full min-h-[87px] min-w-[87px] hover:scale-[1.2]"
              src={item.s3Url}
              alt={item.name}
              onClick={(e) => handleItemClick(item, e.currentTarget as HTMLImageElement)}
              title={`Click to add "${item.name}" to canvas`}
            />
            <button
              onClick={() => handleRemoveItem(item.id)}
              title="Remove"
              className="brand-kit-gallery-remove"
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 16,
                height: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.85)",
                border: "none",
                cursor: "pointer",
                opacity: 0,
                transition: "opacity 0.15s",
              }}
            >
              <Cross2Icon style={{ width: 10, height: 10 }} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BrandKitAssetsPanel;
